import React, { useState } from 'react';
import { Box, Checkbox, IconButton, Popover, PopoverOrigin, Typography } from '@mui/material';
import { ReactComponent as CloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import './scss/column-visibility-popover.scss';

/**
 * 列显隐选项：供公共列选择浮层展示。
 */
export type ColumnVisibilityOption = {
    // 选项唯一标识
    id: string;
    // 选项展示文案
    label: string;
    // 是否禁用当前选项
    disabled?: boolean;
};

/**
 * 触发器渲染参数：用于把任意按钮接成浮层入口。
 */
type ColumnVisibilityTriggerProps = {
    // 触发打开浮层的点击事件
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    // 当前浮层是否已打开
    isOpen: boolean;
    // 触发器需要挂载的无障碍属性
    ariaProps: {
        'aria-haspopup': 'dialog';
        'aria-expanded': boolean;
    };
};

/**
 * 公共列选择浮层属性。
 */
type ColumnVisibilityPopoverProps = {
    // 浮层标题
    title: string;
    // 可切换的列选项
    options: ColumnVisibilityOption[];
    // 当前已选中的列 id 列表
    selectedIds: string[];
    // 点击某个选项时的回调
    onToggle: (option: ColumnVisibilityOption) => void;
    // 自定义触发器渲染函数
    renderTrigger: (props: ColumnVisibilityTriggerProps) => React.ReactNode;
    // 浮层 id，便于测试或无障碍关联
    id?: string;
    // 锚点位置配置
    anchorOrigin?: PopoverOrigin;
    // 弹层变换位置配置
    transformOrigin?: PopoverOrigin;
    // 浮层宽度，默认与当前 Figma 对齐
    width?: number | string;
};

/**
 * 公共列选择浮层：用于承载“选择需要显示的数据”一类交互。
 * @param props - 浮层标题、选项列表、选中态与触发器配置
 * @returns 触发器与对应的列选择浮层
 */
export default function ColumnVisibilityPopover(props: ColumnVisibilityPopoverProps) {
    const {
        title,
        options,
        selectedIds,
        onToggle,
        renderTrigger,
        id = 'column-visibility-popover',
        anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
        transformOrigin = { vertical: 'top', horizontal: 'right' },
        width = 328,
    } = props;

    // 当前触发浮层的锚点元素
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    // 浮层打开状态
    const isOpen = Boolean(anchorEl);

    // 按当前 options 归一化后的选中项，避免外部传入额外 id 影响组件行为
    const normalizedSelectedIdSet = new Set(
        options
            .filter((option) => selectedIds.includes(option.id))
            .map((option) => option.id)
    );

    /**
     * 打开列选择浮层，并记录锚点元素。
     * @param event - 触发器点击事件
     */
    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * 关闭列选择浮层。
     */
    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            {renderTrigger({
                onClick: handleOpen,
                isOpen,
                ariaProps: {
                    'aria-haspopup': 'dialog',
                    'aria-expanded': isOpen,
                },
            })}
            <Popover
                id={id}
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                disableScrollLock
                PaperProps={{ className: 'column-visibility-popover-paper' }}
            >
                <Box className='column-visibility-popover' sx={{ width }}>
                    <Box className='column-visibility-popover-header'>
                        <Typography variant='body1' className='column-visibility-popover-title'>
                            {title}
                        </Typography>
                        <IconButton
                            className='column-visibility-popover-close-btn'
                            onClick={handleClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box className='column-visibility-popover-divider' />
                    <Box className='column-visibility-popover-list'>
                        {options.map((option) => {
                            // 当前选项是否已选中
                            const checked = normalizedSelectedIdSet.has(option.id);
                            return (
                                <label
                                    key={option.id}
                                    className={`column-visibility-popover-item ${checked ? 'is-selected' : ''} ${option.disabled ? 'is-disabled' : ''}`}
                                >
                                    <Typography variant='body2' className='column-visibility-popover-item-label'>
                                        {option.label}
                                    </Typography>
                                    <Checkbox
                                        checked={checked}
                                        disabled={option.disabled}
                                        onChange={() => onToggle(option)}
                                        className={`column-visibility-popover-checkbox ${checked ? 'is-selected' : ''}`}
                                        disableRipple
                                    />
                                </label>
                            );
                        })}
                    </Box>
                </Box>
            </Popover>
        </>
    );
}
